import datePickerLocale from 'antd/es/date-picker/locale/vi_VN';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Card, Table, DatePicker, Button, Space, Typography, 
  message, Spin, Grid, Row, Col, Statistic, Tag, Tooltip, Divider, List, Badge
} from 'antd';
import { 
  ReloadOutlined, ClockCircleOutlined, InfoCircleOutlined, 
  CheckCircleOutlined, WarningOutlined, CalendarOutlined,
  DashboardOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { timekeepingService } from '../services/timekeepingService';
import { authService } from '../services/authService';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

interface ChamCongRow {
  maChamCong: string;
  tenNV: string;
  maKhoa: number;
  thang: number;
  nam: number;
  days: { [key: number]: string };
  secondaryDays: { [key: number]: string }; // Added secondaryDays
  realTimes: { [key: number]: any[] };
  tongHuongLuong: number;
  nghiBuLuyKe: number;
  nghiPhepLuyKe: number;
  tongBHXH: number;
  tongNgayDiLam: number;
  tongNgayTruc: number;
  tongNghiCoLuong: number;
}

const TimekeepingTab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  
  // Intelligent month selection: if today is 26th or later, default to next month (new payroll cycle)
  const [viewingMonth, setViewingMonth] = useState<dayjs.Dayjs>(() => {
    const now = dayjs();
    return now.date() >= 26 ? now.add(1, 'month') : now;
  });

  const [data, setData] = useState<ChamCongRow | null>(null);
  const [dmLoai, setDmLoai] = useState<any[]>([]);
  const [dmLoaiPhu, setDmLoaiPhu] = useState<any[]>([]);
  const [cauHinhCas, setCauHinhCas] = useState<any[]>([]);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const fetchData = useCallback(async (month: dayjs.Dayjs, profile: any) => {
    if (!profile) return;
    
    console.log("Fetching personal timekeeping for:", { macc: profile.macc, maKK: profile.maKK, month: month.format('MM/YYYY') });
    setLoading(true);
    try {
      // Use timekeepingService for all calls to ensure correct permission context
      const [summaryRes, caRes, loaiRes, loaiPhuRes] = await Promise.all([
        timekeepingService.getMySummary(month.month() + 1, month.year()),
        timekeepingService.getCauHinhCa(profile.maKK || 0),
        timekeepingService.getDanhMucLoai(),
        timekeepingService.getDanhMucLoaiPhu()
      ]);

      console.log("Personal Timekeeping Result:", summaryRes.data);

      setDmLoai(loaiRes.data);
      setDmLoaiPhu(loaiPhuRes.data);
      setCauHinhCas(caRes.data || []);
      setData(summaryRes.data || null);
      
      if (!summaryRes.data) {
        message.info('Không tìm thấy dữ liệu chấm công của bạn trong tháng này.');
      }
    } catch (error: any) {
      console.error("Error fetching timekeeping:", error);
      if (error.response?.status === 403) {
        message.error('Bạn không có quyền truy cập dữ liệu này. Vui lòng liên hệ quản trị viên.');
      } else {
        message.error('Lỗi khi tải dữ liệu chấm công');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const profile = await authService.getProfile();
        console.log("User profile loaded:", profile);
        setUserProfile(profile);
        fetchData(viewingMonth, profile);
      } catch (error) {
        message.error('Không thể lấy thông tin người dùng');
      }
    };
    init();
  }, [fetchData]);

  const handleMonthChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      setViewingMonth(date);
      fetchData(date, userProfile);
    }
  };

  const classifyPunchTimes = (punches: any[], config: any): any => {
    if (!punches || punches.length === 0) return null;

    const p0 = config?.gio_BatDau_Sang || '04:00:00';
    const p1 = config?.moc_VaoRa_Sang || '10:00:00';
    const p2 = config?.moc_KetThuc_Sang || '12:30:00';
    const p3 = config?.moc_BatDau_Chieu || '13:00:00';
    const p4 = config?.moc_VaoRa_Chieu || '15:30:00';
    const p5 = config?.gio_KetThuc_Chieu || '22:00:00';

    let vaoSang: any = null;
    let raSang: any = null;
    let vaoChieu: any = null;
    let raChieu: any = null;

    punches.forEach(p => {
      const time = dayjs(p.gioCham);
      const timeStr = time.format('HH:mm:ss');

      if (timeStr >= p0 && timeStr <= p1) {
        if (!vaoSang || time.isBefore(vaoSang)) vaoSang = time;
      } else if (timeStr > p1 && timeStr <= p2) {
        if (!raSang || time.isAfter(raSang)) raSang = time;
      } else if (timeStr >= p3 && timeStr < p4) {
        if (!vaoChieu || time.isBefore(vaoChieu)) vaoChieu = time;
      } else if (timeStr >= p4 && timeStr <= p5) {
        if (!raChieu || time.isAfter(raChieu)) raChieu = time;
      }
    });

    return { vaoSang, raSang, vaoChieu, raChieu };
  };

  const renderDayInfo = (day: number, isPrevMonth: boolean) => {
    if (!data) return null;
    const value = data.days[day];
    const valuePhu = data.secondaryDays ? data.secondaryDays[day] : null;
    const realData = data.realTimes[day] || [];
    const loai = dmLoai.find(l => l.maLoai === value);
    
    const currentDate = isPrevMonth 
      ? viewingMonth.subtract(1, 'month').date(day).startOf('day')
      : viewingMonth.date(day).startOf('day');

    const activeConfig = [...cauHinhCas]
      .filter(c => {
        const configDate = dayjs(c.ngayApDung).startOf('day');
        return configDate.isSame(currentDate) || configDate.isBefore(currentDate);
      })
      .sort((a, b) => dayjs(b.ngayApDung).diff(dayjs(a.ngayApDung)))[0];

    const classified = classifyPunchTimes(realData, activeConfig);

    // Tính tổng số phút được giảm: Mã chính + Mã phụ
    const totalGiam = (loai?.giamGioLamMinutes || 0) + (dmLoaiPhu.find(lp => lp.maLoaiPhu === valuePhu)?.giamGioLamMinutes || 0);

    let punchesContent = null;
    let isWarning = false;
    const displayValue = valuePhu ? `${value || ''} / ${valuePhu}` : (value || '-');

    if (classified) {
      const { vaoSang, raSang, vaoChieu, raChieu } = classified;
      const getStyle = (time: dayjs.Dayjs | null, type: 'in_s' | 'out_s' | 'in_c' | 'out_c') => {
        if (!time || !activeConfig || !loai || loai.heSoCong <= 0) return {};

        let style: any = {};
        if (type === 'in_s') {
          const baseIn = dayjs(time.format('YYYY-MM-DD') + ' ' + activeConfig.gioSang_Vao);
          const lateMinutes = time.diff(baseIn, 'minute');
          if (lateMinutes > 5) {
            style = { color: '#ff4d4f', fontWeight: 'bold' }; // đỏ
          } else if (lateMinutes > 0) {
            style = { color: '#faad14', fontWeight: 'bold' }; // cam
          }
        } else if (type === 'out_s') {
          const baseOut = dayjs(time.format('YYYY-MM-DD') + ' ' + activeConfig.gioSang_Ra).subtract(totalGiam, 'minute');
          if (time.isBefore(baseOut, 'minute')) {
            style = { color: '#faad14', fontWeight: 'bold' }; // cam
          }
        } else if (type === 'in_c') {
          const baseIn = dayjs(time.format('YYYY-MM-DD') + ' ' + activeConfig.gioChieu_Vao);
          const lateMinutes = time.diff(baseIn, 'minute');
          if (lateMinutes > 5) {
            style = { color: '#ff4d4f', fontWeight: 'bold' }; // đỏ
          } else if (lateMinutes > 0) {
            style = { color: '#faad14', fontWeight: 'bold' }; // cam
          }
        } else if (type === 'out_c') {
          const baseOut = dayjs(time.format('YYYY-MM-DD') + ' ' + activeConfig.gioChieu_Ra).subtract(totalGiam, 'minute');
          if (time.isBefore(baseOut, 'minute')) {
            style = { color: '#faad14', fontWeight: 'bold' }; // cam
          }
        }
        return style;
      };

      if (vaoSang && raChieu && !raSang && !vaoChieu) {
        punchesContent = (
          <div style={{ fontSize: '15px' }}>
            <span style={getStyle(vaoSang, 'in_s')}>{vaoSang.format('HH:mm')}</span> - <span style={getStyle(raChieu, 'out_c')}>{raChieu.format('HH:mm')}</span>
          </div>
        );
      } else {
        punchesContent = (
          <div style={{ fontSize: '15px', lineHeight: '1.2' }}>
            {(vaoSang || raSang) && (
              <div>S: <span style={getStyle(vaoSang, 'in_s')}>{vaoSang?.format('HH:mm') || '--'}</span>-<span style={getStyle(raSang, 'out_s')}>{raSang?.format('HH:mm') || '--'}</span></div>
            )}
            {(vaoChieu || raChieu) && (
              <div>C: <span style={getStyle(vaoChieu, 'in_c')}>{vaoChieu?.format('HH:mm') || '--'}</span>-<span style={getStyle(raChieu, 'out_c')}>{raChieu?.format('HH:mm') || '--'}</span></div>
            )}
          </div>
        );
      }
    } else if (value && loai && (loai.nhomLoai === 1 || loai.nhomLoai === 2)) {
      isWarning = true;
    }

    return { value, displayValue, punchesContent, isWarning, dayOfWeek: currentDate.format('dd'), date: currentDate };
  };

  // Logic Desktop Table
  const columns = useMemo(() => {
    const prevMonth = viewingMonth.subtract(1, 'month');
    const daysInPrev = prevMonth.daysInMonth();
    const cols: any[] = [{ title: 'Thông tin', dataIndex: 'type', key: 'type', fixed: 'left', width: 100 }];

    for (let i = 26; i <= daysInPrev; i++) {
      const date = prevMonth.date(i);
      cols.push({
        title: <div style={{ fontSize: '11px' }}>{i}<br/><small>{date.format('dd')}</small></div>,
        key: `prev-${i}`,
        align: 'center',
        width: 70,
        render: (_: any, record: any) => {
          const info = renderDayInfo(i, true);
          if (record.type === 'Mã công') return <strong>{info?.displayValue}</strong>;
          return info?.punchesContent;
        }
      });
    }
    for (let i = 1; i <= 25; i++) {
      const date = viewingMonth.date(i);
      cols.push({
        title: <div style={{ fontSize: '11px' }}>{i}<br/><small>{date.format('dd')}</small></div>,
        key: `curr-${i}`,
        align: 'center',
        width: 70,
        render: (_: any, record: any) => {
          const info = renderDayInfo(i, false);
          if (record.type === 'Mã công') return <strong>{info?.displayValue}</strong>;
          return info?.punchesContent;
        }
      });
    }
    return cols;
  }, [viewingMonth, data, dmLoai, dmLoaiPhu, cauHinhCas]);

  const desktopTableData = [
    { key: 'code', type: 'Mã công' },
    { key: 'time', type: 'Giờ vân tay' }
  ];

  // Logic Mobile Timeline
  const renderMobileTimeline = () => {
    if (!data) return <EmptyData />;
    const days: any[] = [];
    const prevMonth = viewingMonth.subtract(1, 'month');
    const daysInPrev = prevMonth.daysInMonth();

    for (let i = 26; i <= daysInPrev; i++) days.push({ day: i, isPrev: true });
    for (let i = 1; i <= 25; i++) days.push({ day: i, isPrev: false });

    return (
      <List
        dataSource={days}
        renderItem={(item) => {
          const info = renderDayInfo(item.day, item.isPrev);
          const isWeekend = info?.date.day() === 0 || info?.date.day() === 6;
          return (
            <List.Item style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                <div style={{ width: '50px', textAlign: 'center', marginRight: '12px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: isWeekend ? '#ff4d4f' : 'inherit' }}>{item.day}</div>
                  <div style={{ fontSize: '13px', color: '#8c8c8c' }}>{info?.date.format('dddd')}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <Badge status={info?.isWarning ? "error" : "success"} text={<Text strong>{info?.displayValue || '-'}</Text>} />
                    <Text type="secondary" style={{ fontSize: '13px' }}>{dmLoai.find(l => l.maLoai === info?.value)?.tenLoai}</Text>
                  </div>
                  {info?.punchesContent || (info?.isWarning ? <Text type="danger" style={{ fontSize: '13px' }}><WarningOutlined /> Thiếu dữ liệu vân tay</Text> : <Text type="secondary" style={{ fontSize: '13px' }}>Không có dữ liệu vân tay</Text>)}
                </div>
              </div>
            </List.Item>
          );
        }}
      />
    );
  };

  const EmptyData = () => (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <DashboardOutlined style={{ fontSize: '48px', color: '#f0f0f0', marginBottom: '16px' }} />
      <p style={{ color: '#8c8c8c' }}>Chưa có dữ liệu chấm công tháng này</p>
    </div>
  );

  return (
    <div style={{ padding: isMobile ? '0' : '24px' }}>
      <Card bordered={false} bodyStyle={{ padding: isMobile ? '16px' : '24px' }}>
        <Row gutter={[16, 16]} align="middle" justify="space-between" style={{ marginBottom: '24px' }}>
          <Col xs={24} md={12}>
            <Title level={4} style={{ margin: 0 }}><CalendarOutlined /> Chấm công cá nhân</Title>
            <Text type="secondary">Chu kỳ từ ngày 26 tháng trước đến ngày 25 tháng này</Text>
          </Col>
          <Col xs={24} md={12} style={{ textAlign: isMobile ? 'left' : 'right' }}>
            <Space size="middle">
              <DatePicker 
                picker="month" 
                format="MM/YYYY" 
                value={viewingMonth} 
                onChange={handleMonthChange} 
                allowClear={false}
                locale={datePickerLocale}
                inputReadOnly
                style={{ width: isMobile ? '100%' : '150px' }}
              />
              <Button icon={<ReloadOutlined />} onClick={() => fetchData(viewingMonth, userProfile)} loading={loading}>Lấy lại dữ liệu</Button>
            </Space>
          </Col>
        </Row>

        {data && (
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={12} sm={6} md={4}>
              <Card size="small" style={{ textAlign: 'center', background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                <Statistic title="Công hưởng lương" value={data.tongHuongLuong} precision={4} valueStyle={{ color: '#389e0d' }} />
              </Card>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Card size="small" style={{ textAlign: 'center', background: '#e6f7ff', border: '1px solid #91d5ff' }}>
                <Statistic title="Công đi làm" value={data.tongNgayDiLam} precision={4} valueStyle={{ color: '#096dd9' }} />
              </Card>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Card size="small" style={{ textAlign: 'center', background: '#fff7e6', border: '1px solid #ffd591' }}>
                <Statistic title="Nghỉ bù còn" value={data.nghiBuLuyKe} precision={4} valueStyle={{ color: data.nghiBuLuyKe >= 0 ? '#d46b08' : '#cf1322' }} />
              </Card>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Card size="small" style={{ textAlign: 'center', background: '#f0f5ff', border: '1px solid #adc6ff' }}>
                <Statistic title="Nghỉ phép còn" value={data.nghiPhepLuyKe} precision={4} valueStyle={{ color: '#1d39c4' }} />
              </Card>
            </Col>
            {!isMobile && (
              <>
                <Col md={4}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic title="Hưởng BHXH" value={data.tongBHXH} precision={4} />
                  </Card>
                </Col>
                <Col md={4}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic title="Tổng trực" value={data.tongNgayTruc} precision={4} />
                  </Card>
                </Col>
              </>
            )}
          </Row>
        )}

        <Divider style={{ margin: '12px 0 24px 0' }} />

        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" tip="Đang tải dữ liệu..." /></div>
        ) : (
          <>
            {!isMobile ? (
              <Table
                dataSource={desktopTableData}
                columns={columns}
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <EmptyData /> }}
              />
            ) : (
              renderMobileTimeline()
            )}
          </>
        )}
        
        <div style={{ marginTop: '24px' }}>
          <Alert
            message="Ghi chú"
            description={
              <ul style={{ paddingLeft: '20px', margin: 0, fontSize: '13px' }}>
                <li><b>Chu kỳ tính lương</b>: Bắt đầu từ ngày 26 tháng trước đến ngày 25 tháng này.</li>
                <li><Text type="danger" strong>Chữ màu đỏ</Text>: Đi muộn hoặc về sớm so với quy định ca làm việc.</li>
                <li><Badge status="error" />: Có mã công đi làm nhưng máy vân tay không ghi nhận dữ liệu (Cần liên hệ quản lý).</li>
                <li>Các con số lũy kế được tính dựa trên mốc chốt số dư gần nhất của bạn.</li>
              </ul>
            }
            type="info"
            showIcon
          />
        </div>
      </Card>
    </div>
  );
};

const Alert: React.FC<{ message: string, description: React.ReactNode, type: any, showIcon: boolean, style?: any }> = ({ message, description, type, showIcon, style }) => {
  return (
    <div style={{ ...style, display: 'flex', padding: '12px', borderRadius: '8px', background: '#e6f7ff', border: '1px solid #91d5ff' }}>
      {showIcon && <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '18px', marginRight: '12px', marginTop: '2px' }} />}
      <div>
        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{message}</div>
        <div style={{ fontSize: '13px' }}>{description}</div>
      </div>
    </div>
  );
};

export default TimekeepingTab;
